(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editorMotionNode = document.getElementById('EaEditorMotion');
  const editorMotion = editorMotionNode ? JSON.parse(editorMotionNode.textContent) : null;
  if (editorMotion?.mode === 'off') return;
  const customMotion = editorMotion?.mode === 'custom';
  const activeAnimations = new Set();
  const revealedElements = new WeakSet();
  const pendingElements = new Map();
  const scrollImages = new Map();
  const visibleImages = new Set();
  const revealSelector = '[data-motion], [data-motion-group] > *';
  const ease = customMotion ? editorMotion.ease : 'cubic-bezier(.22,1,.36,1)';
  let scrollFrame = 0;

  function animate(element, frames, options = {}) {
    if (!element || reducedMotion.matches || !element.animate) return;
    const animation = element.animate(frames, { duration: customMotion ? editorMotion.duration : 950, easing: ease, fill: 'backwards', ...options });
    activeAnimations.add(animation);
    const release = () => activeAnimations.delete(animation);
    animation.addEventListener('finish', release, { once: true });
    animation.addEventListener('cancel', release, { once: true });
  }

  function reveal(element) {
    const isImage = element.dataset.motion === 'image';
    const isIntro = element.dataset.motion === 'intro' || element.parentElement.dataset.motionGroup === 'intro';
    const transform = customMotion ? `translateY(${editorMotion.distance}px)` : isImage ? 'translateY(32px) scale(.96)' : 'translateY(48px)';
    const delay = pendingElements.get(element) || 0;
    element.classList.add('is-motion-revealed');
    animate(element, [{ opacity: isIntro ? 1 : .12, transform }, { opacity: 1, transform: 'none' }], { delay });
    revealedElements.add(element);
    pendingElements.delete(element);
  }

  let observer;
  let imageObserver;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting || reducedMotion.matches || !pendingElements.has(entry.target)) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: .08, rootMargin: '0px 0px -32px 0px' });
    imageObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!scrollImages.has(entry.target)) return;
        if (entry.isIntersecting) visibleImages.add(entry.target);
        else visibleImages.delete(entry.target);
      });
      scheduleScroll();
    });
  }

  function updateScroll() {
    scrollFrame = 0;
    if (reducedMotion.matches || document.hidden) return;
    const viewportHeight = window.innerHeight;
    const updates = [...visibleImages].map(element => {
      const rect = element.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (viewportHeight + rect.height)));
      return { image: scrollImages.get(element), offset: (progress - .5) * (customMotion ? editorMotion.parallaxStrength : 6) };
    });
    updates.forEach(({ image, offset }) => {
      image.style.transform = `translate3d(0, ${offset.toFixed(3)}%, 0) scale(1.08)`;
    });
  }

  function scheduleScroll() {
    if (scrollFrame || reducedMotion.matches || document.hidden || !visibleImages.size) return;
    scrollFrame = requestAnimationFrame(updateScroll);
  }

  function initialize(scope) {
    if (!observer || reducedMotion.matches) return;
    scope.querySelectorAll(revealSelector).forEach(element => {
      if (revealedElements.has(element) || pendingElements.has(element)) return;
      const group = element.parentElement;
      let delay = Number(element.dataset.motionDelay) || 0;
      if (group.hasAttribute('data-motion-group')) delay = [...group.children].indexOf(element) * (customMotion ? editorMotion.stagger : 85);
      pendingElements.set(element, Math.min(delay, 340));
      observer.observe(element);
    });
    if (editorMotion?.parallax === 'off') return;
    scope.querySelectorAll('[data-motion-scroll]').forEach(element => {
      if (scrollImages.has(element)) return;
      const image = element.querySelector('img');
      if (!image) return;
      scrollImages.set(element, image);
      imageObserver.observe(element);
    });
  }

  initialize(document);
  window.addEventListener('scroll', scheduleScroll, { passive: true });
  window.addEventListener('resize', scheduleScroll, { passive: true });
  document.addEventListener('visibilitychange', scheduleScroll);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(revealSelector).forEach(element => {
      observer?.unobserve(element);
      pendingElements.delete(element);
    });
    event.target.querySelectorAll('[data-motion-scroll]').forEach(element => {
      imageObserver?.unobserve(element);
      scrollImages.delete(element);
      visibleImages.delete(element);
    });
    activeAnimations.forEach(animation => {
      if (event.target.contains(animation.effect?.target)) animation.cancel();
    });
  });

  reducedMotion.addEventListener('change', event => {
    if (!event.matches) {
      initialize(document);
      return;
    }
    observer?.disconnect();
    imageObserver?.disconnect();
    cancelAnimationFrame(scrollFrame);
    scrollFrame = 0;
    pendingElements.clear();
    visibleImages.clear();
    scrollImages.forEach(image => image.style.removeProperty('transform'));
    scrollImages.clear();
    activeAnimations.forEach(animation => animation.cancel());
    activeAnimations.clear();
  });
})();
